import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/context/AuthProvider"; 

export default function RegisteredDashboard() {
  const navigate = useNavigate();
  
  /**
   * 🎯 ARCHITECTURAL SYNC
   * Pulling matrix, isInitialising, and user from AuthProvider.
   * isInitialising handles the boot sequence (F5).
   * matrix presence handles the post-login sequence.
   */
  const { matrix, isInitialising, user } = useAuth(); 

  // --- 🛰️ ENVIRONMENT CONFIGURATION ---
  // Using the specific keys provided in your .env
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";
  const DEFAULT_LABEL = process.env.REACT_APP_SITE_DEFAULTLABEL || "EFRAG";
  
  // Role Mapping (Ensuring strict casing matches from .env)
  const GA_ROLE = (process.env.REACT_APP_GA || "GLOBALADMIN").toUpperCase();
  const SA_ROLE = (process.env.REACT_APP_SA || "SITEADMIN").toUpperCase();

  /**
   * 🎯 AUTHORITY CHECK
   * Calculates the role based on the matrix provided by the AuthProvider.
   * Handles casing sensitivity (roleCode vs RoleCode) from the BFF.
   */
  const isGlobalAdmin = matrix?.some(
    (m) => String(m.roleCode || m.RoleCode || "").toUpperCase() === GA_ROLE
  );

  const isSiteAdmin = matrix?.some(
    (m) => String(m.roleCode || m.RoleCode || "").toUpperCase() === SA_ROLE
  );

  /**
   * 🎯 THE ORDER OF OPERATIONS GATE
   * We do not render the dashboard until the AuthProvider signals that
   * initialization is complete. If we have a user identity but the security 
   * matrix is still empty, we show the loading state to prevent a race condition.
   */
  if (isInitialising || (user && (!matrix || matrix.length === 0))) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p className="text-muted fw-bold">Synchronizing {DEFAULT_LABEL} Security Matrix...</p>
        </div>
      </div>
    );
  }

  const capabilities = [
    {
      //name: `${DEFAULT_LABEL} Details`, // Uses EFRAG from .env
      name: `Corporate Details`, // Uses EFRAG from .env
      icon: "/icons/2D2C31_user-writer.png",
      path: "/registered/corporate-details", 
    },
    {
      name: "Data-Entry",
      icon: "/icons/2D2C31_data-warehouse.png",
      path: "/registered/data-entry", 
    },
    {
      name: "Evidences",
      icon: "/icons/2D2C31_camera.png",
      path: "/capabilities/uploads",
    },
    {
      name: "Monitoring",
      icon: "/icons/2D2C31_camera-movie.png",
      path: "/capabilities/monitoring",
    },
    {
      name: "Draft and reviews",
      icon: "/icons/2D2C31_analyse-alt.png",
      path: "/capabilities/reviews",
    },
    {
      name: "Commentaries & Publication",
      icon: "/icons/2D2C31_microphone-alt.png",
      path: "/capabilities/commentaries",
    },
    {
      name: "Site Administration",
      icon: "/icons/2D2C31_SiteAdmin.png",
      path: "/registered/site-admin",
      restricted: true, // Requires Admin level
    },
  ];

  /**
   * 🎯 DYNAMIC FILTERING
   * Only includes restricted items if the user identity has the Global Admin 
   * OR Site Admin role as defined in your .env variables.
   */
  const filteredCapabilities = capabilities.filter(cap => {
    if (cap.restricted && !isGlobalAdmin && !isSiteAdmin) return false;
    return true;
  });

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-4">
              <div className="col">
                <h2 className="h4 fw-bold">{SITE_NAME}</h2>
                <p className="text-muted small">
                  Welcome back, <strong>{user?.userName || user?.email || "User"}</strong>. 
                  Choose a capability below.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {filteredCapabilities.map((cap, index) => (
                <div key={index} className="col-12 col-sm-6 col-md-4">
                  <div
                    className="card h-100 text-center shadow-sm border dashboard-card"
                    onClick={() => navigate(cap.path)}
                    style={{ 
                      cursor: "pointer", 
                      transition: "all 0.2s ease-in-out",
                      border: "1px solid #dee2e6"
                    }}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center py-4">
                      <img
                        src={cap.icon}
                        alt={cap.name}
                        className="mb-3"
                        style={{ width: "64px", height: "64px", objectFit: "contain" }}
                      />
                      <h6 className="card-title mb-0 fw-bold">{cap.name}</h6>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}